import { DayObject, TimeOfDay } from "@/types/date"
import { EventActorMinimal, EventActorSummary } from "@/types/event-actor"
import {
  AirbnbListing,
  Event,
  EventParams,
  EventSchedule,
  Place,
  PlacePlaceholder,
  RelatedPlacePlaceholders,
  SampleQuestion,
} from "@/types/place"
import { HotelQuote } from "@/types/price-quote"
import { TravelerGroup } from "@/types/trip"
import pluralize from "pluralize"

import { timeOfDayToKey } from "@/lib/business-hours-utils"
import {
  hasEmbeddedProvider,
  isAirport,
  isEvent,
  isHotel,
  isLocation,
  isRestaurant,
} from "@/lib/entity-utils"
import { formatDayObjectAsMMDDYYYY } from "@/lib/format-utils"
import { isDomainMatch, joinUrl, tryParseUrl } from "@/lib/url-utils"
import { getConstantEmptyArray } from "@/lib/utils/collections"
import { beforeTimestamp, dayObjectAsUrlParam, now } from "./date-utils"

export type PlaceDateKind = "stay" | "day"

export function placeDateKind(place: Place | PlacePlaceholder): PlaceDateKind {
  return isHotel(place) || isAirport(place) || isLocation(place)
    ? "stay"
    : "day"
}

// Returns a constant empty array that's immutable at runtime.
export function getEmptyPlaceArray(): Place[] {
  return getConstantEmptyArray()
}

export function isAnsweredQuestion(q: SampleQuestion): boolean {
  return !!q.answer?.trim() && !!q.answer_summary?.trim()
}

export function processRelatedPlaceholderData(
  data: RelatedPlacePlaceholders
): PlacePlaceholder[] {
  const results: PlacePlaceholder[] = []
  if (data.similar) {
    results.push(...data.similar)
  }
  if (data.nearby.attractions) {
    results.push(...data.nearby.attractions)
  }
  if (data.nearby.hotels) {
    results.push(...data.nearby.hotels)
  }
  if (data.nearby.restaurants) {
    results.push(...data.nearby.restaurants)
  }
  return results
}

export function isViatorUrl(url: string): boolean {
  return isDomainMatch(url, "viator.com")
}

export function hasCheckFlights(
  place: Place,
  bookDirectlyOnly: boolean
): boolean {
  return isAirport(place) && !bookDirectlyOnly
}

export function isCallToBookHotel(place: Place): boolean {
  return (
    isHotel(place) &&
    place.detailed &&
    !place.booking_url &&
    !hasEmbeddedProvider(place) &&
    Boolean(place.phone)
  )
}

// A vacation rental, known from a place summary too (Airbnb listings are in
// this category)
export function isShortTermRental(place: Place): boolean {
  return isHotel(place) && !!place.categories?.includes("short_term_rental")
}

export function getAirbnbListing(place: Place): AirbnbListing | undefined {
  return isHotel(place) && place.detailed
    ? place.short_term_rental?.airbnb
    : undefined
}

// Whether to ask our hotel providers for rates: they have none for an Airbnb
// listing, whose price comes from Airbnb itself.
export function hasProviderHotelRates(place: Place): boolean {
  return isHotel(place) && !getAirbnbListing(place)
}

// The traveler's recent Airbnb quote for exactly the dates and party picked
// here. The hotel guest picker has no pets stepper, so a picked party with no
// pets is taken as "pets unsaid" and also matches a quote priced with pets
// (one priced without pets wins when both exist); a picked pets count must match.
export function findAirbnbQuote(
  quotes: HotelQuote[] | undefined,
  start: DayObject | undefined,
  end: DayObject | undefined,
  travelers: TravelerGroup | undefined
): HotelQuote | undefined {
  if (!quotes || !start || !end || !travelers) return undefined
  const matches = quotes.filter(
    (q) =>
      q.dates.check_in === dayObjectAsUrlParam(start) &&
      q.dates.check_out === dayObjectAsUrlParam(end) &&
      q.people.adults === travelers.adult_count &&
      q.people.children === travelers.child_count &&
      (travelers.pet_count === 0 ||
        (q.str_listing?.pets ?? 0) === travelers.pet_count)
  )
  return (
    matches.find((q) => (q.str_listing?.pets ?? 0) === travelers.pet_count) ??
    matches[0]
  )
}

// The listing on airbnb.com for the dates and guests the traveler picked, in
// the query params Airbnb's own listing links use. A matching quote's booking
// URL already carries its whole party (pets included), so it wins.
export function getAirbnbListingUrl(
  listing: AirbnbListing,
  start: DayObject | undefined,
  end: DayObject | undefined,
  travelers: TravelerGroup | undefined,
  quote?: HotelQuote
): string {
  if (quote?.booking_url) return quote.booking_url
  const url = new URL(listing.listing_url)
  if (start && end) {
    url.searchParams.set("check_in", dayObjectAsUrlParam(start))
    url.searchParams.set("check_out", dayObjectAsUrlParam(end))
  }
  if (travelers) {
    url.searchParams.set("adults", String(travelers.adult_count))
    const optional = {
      children: travelers.child_count,
      infants: travelers.infant_count,
      pets: travelers.pet_count,
    }
    for (const [param, count] of Object.entries(optional)) {
      if (count > 0) url.searchParams.set(param, String(count))
    }
  }
  return url.toString()
}

export function isBookableHotel(
  place: Place,
  bookDirectOnly: boolean
): boolean {
  return isHotel(place) && !bookDirectOnly
}

export function hasExternalBookingUrl(
  place: Place,
  bookDirectOnly: boolean
): boolean {
  if (isEvent(place) && place.schedules?.data?.[0]?.booking_url) {
    return true
  }

  return (
    Boolean(place.booking_url) &&
    !bookDirectOnly &&
    (place.booking_url !== place.url || isViatorUrl(place.booking_url ?? ""))
  )
}

export function isViatorWithNoDirectLink(
  place: Place,
  bookDirectOnly: boolean
): boolean {
  return (
    bookDirectOnly &&
    isViatorUrl(place.url ?? "") &&
    (!place.booking_url || isViatorUrl(place.booking_url))
  )
}

export function hasDirectBookingUrl(
  place: Place,
  bookDirectOnly: boolean
): boolean {
  if (isViatorWithNoDirectLink(place, bookDirectOnly)) {
    return false
  }
  return bookDirectOnly && Boolean(place.url)
}

export function getRipeBookingId(place: Place): string | undefined {
  if (isHotel(place) && place.detailed) {
    return place.organization_properties?.ripe_id
  }
}

export function getAresBookingId(place: Place): string | undefined {
  if (isHotel(place) && place.detailed) {
    return place.organization_properties?.ares_id
  }
}

export function getRipeBookingUrl(
  place: Place,
  start: DayObject | undefined,
  end: DayObject | undefined,
  travelers: TravelerGroup | undefined,
  urlPrefix: string | undefined
): string | undefined {
  if (!urlPrefix) {
    return place.url
  }
  const checkinDate = start
    ? encodeURIComponent(formatDayObjectAsMMDDYYYY(start))
    : ""
  const checkoutDate = end
    ? encodeURIComponent(formatDayObjectAsMMDDYYYY(end))
    : ""
  const path = `${getRipeBookingId(place)}?Checkin=${checkinDate}&Checkout=${checkoutDate}&GuestsAdult=${travelers?.adult_count}&GuestsChildren=${travelers?.child_count}`
  return joinUrl(urlPrefix, path)
}

// aRes url e.g. https://hotels.sandiego.org/hotel/single/246/70945?Search[checkInDate]=09/15/2026&…
export function getAresBookingUrl(
  place: Place,
  start: DayObject | undefined,
  end: DayObject | undefined,
  travelers: TravelerGroup | undefined,
  urlPrefix: string | undefined
): string | undefined {
  if (!urlPrefix) {
    return place.url
  }
  const params = new URLSearchParams()
  if (start) {
    params.set("Search[checkInDate]", formatDayObjectAsMMDDYYYY(start))
  }
  if (end) {
    params.set("Search[checkOutDate]", formatDayObjectAsMMDDYYYY(end))
  }
  if (travelers) {
    params.set(
      "Search[roomOccupancies][0][numberOfAdults]",
      String(travelers.adult_count)
    )
    params.set(
      "Search[roomOccupancies][0][numberOfChildren]",
      String(travelers.child_count)
    )
  }
  const query = params.toString()
  const id = getAresBookingId(place)
  return joinUrl(urlPrefix, query ? `${id}?${query}` : `${id}`)
}

export function shouldShowBookButton(
  place: Place,
  bookDirectOnly: boolean
): boolean {
  return (
    hasCheckFlights(place, bookDirectOnly) ||
    isLocation(place) ||
    isCallToBookHotel(place) ||
    isBookableHotel(place, bookDirectOnly) ||
    hasExternalBookingUrl(place, bookDirectOnly) ||
    hasDirectBookingUrl(place, bookDirectOnly)
  )
}

export function getDefaultEventParams(event: Event): EventParams | undefined {
  if (!event.schedules?.data?.length) {
    return undefined
  }

  return {
    at: event.schedules.data[0].start_at.epoch_millis,
  }
}

/**
 * find the schedule that matches the given scheduleStartAt timestamp.
 * returns the schedule with start_at.epoch_millis that matches scheduleStartAt,
 * or the closest schedule if no exact match is found
 */
export function findMatchingSchedule(
  schedules: EventSchedule[],
  scheduleStartAt: number
): EventSchedule | undefined {
  if (!schedules?.length) {
    return undefined
  }

  // try to find exact match first
  const exactMatch = schedules.find(
    (schedule) => schedule.start_at.epoch_millis === scheduleStartAt
  )
  if (exactMatch) {
    return exactMatch
  }

  // if no exact match, find the closest schedule
  let closest: EventSchedule | undefined
  let minDiff = Infinity

  for (const schedule of schedules) {
    const diff = Math.abs(schedule.start_at.epoch_millis - scheduleStartAt)
    if (diff < minDiff) {
      minDiff = diff
      closest = schedule
    }
  }

  return closest
}

/**
 * get the schedule to display for an event, preferring the one matching scheduleStartAt if provided
 */
export function getEventSchedule(
  event: Event,
  scheduleStartAt?: number
): EventSchedule | undefined {
  if (!event.schedules?.data?.length) {
    return undefined
  }

  if (scheduleStartAt !== undefined) {
    const matching = findMatchingSchedule(event.schedules.data, scheduleStartAt)
    if (matching) {
      return matching
    }
  }

  // fall back to first schedule
  return event.schedules.data[0]
}

/*
  Note that the logic here should match the logic in PlaceDetailBookButton.

  The reason we have this function is to put the cta button label in Amplitude during
  ViewPlaceDetailSlideOut event.
  */
export const PLACE_DETAIL_BOOKING_TYPE_FLIGHT = "flight"
export const PLACE_DETAIL_BOOKING_TYPE_RECOMMEND_HOTELS = "recommend hotel"
export const PLACE_DETAIL_BOOKING_TYPE_CALL_TO_BOOK_HOTEL = "call to book hotel"
export const PLACE_DETAIL_BOOKING_TYPE_BOOKABLE_HOTEL = "bookable hotel"
export const PLACE_DETAIL_BOOKING_TYPE_EXTERNAL = "external"
export const PLACE_DETAIL_BOOKING_TYPE_DIRECT = "direct"
export const PLACE_DETAIL_BOOKING_TYPE_RIPE = "ripe"
export const PLACE_DETAIL_BOOKING_TYPE_ARES = "ares"
export const PLACE_DETAIL_BOOKING_TYPE_AIRBNB = "airbnb"

export function placeDetailCTA(
  place: Place,
  bookDirectlyOnly: boolean,
  hasHotelPrice = false
): { bookingType: string | null; ctaButtonLabel: string | null } {
  if (hasCheckFlights(place, bookDirectlyOnly)) {
    return {
      bookingType: PLACE_DETAIL_BOOKING_TYPE_FLIGHT,
      ctaButtonLabel: "Check flights",
    }
  }
  if (isLocation(place)) {
    return {
      bookingType: PLACE_DETAIL_BOOKING_TYPE_RECOMMEND_HOTELS,
      ctaButtonLabel: "Recommend hotels",
    }
  }
  if (getAirbnbListing(place)) {
    return {
      bookingType: PLACE_DETAIL_BOOKING_TYPE_AIRBNB,
      ctaButtonLabel: "View on Airbnb",
    }
  }
  if (isCallToBookHotel(place) && place.detailed && !hasHotelPrice) {
    return {
      bookingType: PLACE_DETAIL_BOOKING_TYPE_CALL_TO_BOOK_HOTEL,
      ctaButtonLabel: "Call to book",
    }
  }
  if (isBookableHotel(place, bookDirectlyOnly)) {
    return {
      bookingType: PLACE_DETAIL_BOOKING_TYPE_BOOKABLE_HOTEL,
      ctaButtonLabel: "Check availability",
    }
  }
  if (hasExternalBookingUrl(place, bookDirectlyOnly)) {
    if (isRestaurant(place)) {
      return {
        bookingType: PLACE_DETAIL_BOOKING_TYPE_EXTERNAL,
        ctaButtonLabel: "Reserve",
      }
    } else if (isEvent(place)) {
      return {
        bookingType: PLACE_DETAIL_BOOKING_TYPE_EXTERNAL,
        ctaButtonLabel: "View tickets",
      }
    } else {
      return {
        bookingType: PLACE_DETAIL_BOOKING_TYPE_EXTERNAL,
        ctaButtonLabel: "Check availability",
      }
    }
  }
  if (hasDirectBookingUrl(place, bookDirectlyOnly)) {
    if (getRipeBookingId(place)) {
      return {
        bookingType: PLACE_DETAIL_BOOKING_TYPE_RIPE,
        ctaButtonLabel: "Check availability",
      }
    } else if (getAresBookingId(place)) {
      return {
        bookingType: PLACE_DETAIL_BOOKING_TYPE_ARES,
        ctaButtonLabel: "Check availability",
      }
    } else {
      return {
        bookingType: PLACE_DETAIL_BOOKING_TYPE_DIRECT,
        ctaButtonLabel: "Website",
      }
    }
  }
  return { bookingType: null, ctaButtonLabel: null }
}

type SportMatchEventActor = EventActorSummary | EventActorMinimal

export function isSportMatch(place: Place): place is Event & {
  event_actors: [SportMatchEventActor, SportMatchEventActor]
} {
  return Boolean(
    isEvent(place) &&
    place.categories?.includes("sports") &&
    place.event_actors &&
    place.event_actors.length === 2
  )
}

export function isEventInPast(place: Place): boolean {
  return Boolean(
    isEvent(place) &&
    place.schedules?.data?.[0]?.start_at &&
    beforeTimestamp(place.schedules.data[0].start_at, now())
  )
}

export function isEventSoldOut(place: Place): boolean {
  return (
    isEvent(place) && place.schedules?.data?.[0]?.ticket_status === "sold_out"
  )
}

type ReservationUrlProvider =
  | "opentable"
  | "resy"
  | "tock"
  | "thefork"
  | "sevenrooms"
  | "yelp"

const RESERVATION_PROVIDER_DISPLAY_NAMES: Record<
  ReservationUrlProvider,
  string
> = {
  opentable: "OpenTable",
  resy: "Resy",
  tock: "Tock",
  thefork: "TheFork",
  sevenrooms: "SevenRooms",
  yelp: "Yelp",
}

function reservationUrlHostProvider(
  host: string
): ReservationUrlProvider | undefined {
  // "opentable"/"thefork" must be the registrable label: an optional co./com. second
  // level plus a 2-3 letter TLD covers their international ccTLDs (opentable.co.uk,
  // opentable.com.au, thefork.es, …) while rejecting opentable.<attacker>.com. This is
  // display-only and human-in-the-loop (it labels a "Reserve on …" link the user clicks),
  // so a broad structural match is fine here; the strict, fail-closed allow-list that
  // gates the auto-navigating agent lives in the backend Agent::Booking::ProviderDomains.
  if (/(?:^|\.)opentable\.(?:com?\.)?[a-z]{2,3}$/.test(host)) return "opentable"
  if (/(?:^|\.)thefork\.(?:com?\.)?[a-z]{2,3}$/.test(host)) return "thefork"
  return undefined
}

export function reservationUrlProvider(
  url: string
): ReservationUrlProvider | undefined {
  const urlObj = tryParseUrl(url)
  if (urlObj) {
    // Drop a trailing FQDN dot ("opentable.com." -> "opentable.com") so it classifies the
    // same as the backend, which normalizes it away.
    const hostProvider = reservationUrlHostProvider(
      urlObj.hostname.toLowerCase().replace(/\.$/, "")
    )
    if (hostProvider) return hostProvider
  }
  if (isDomainMatch(url, "resy.com")) return "resy"
  if (isDomainMatch(url, "exploretock.com")) return "tock"
  if (isDomainMatch(url, "sevenrooms.com")) return "sevenrooms"
  if (isDomainMatch(url, "yelp.com")) return "yelp"
  return undefined
}

export function reservationPlatformMenuLabel(url: string): string | undefined {
  const provider = reservationUrlProvider(url)
  if (!provider) return undefined
  return `Reserve on ${RESERVATION_PROVIDER_DISPLAY_NAMES[provider]}`
}

export type RestaurantReserveCta =
  | { mode: "menu"; reservationPlatformLabel: string }
  | { mode: "reserveForMe" }
  | { mode: "directProvider" }
  | { mode: "none" }

export function restaurantReserveCta({
  enabled,
  reservable,
  booked,
  bookingUrl,
  allowReserveForMe = true,
}: {
  enabled: boolean
  reservable: boolean
  booked: boolean
  bookingUrl?: string
  allowReserveForMe?: boolean
}): RestaurantReserveCta {
  if (booked) return { mode: "none" }
  if (!reservable || !enabled) return { mode: "directProvider" }

  const reservationPlatformLabel = bookingUrl
    ? reservationPlatformMenuLabel(bookingUrl)
    : undefined

  if (reservationPlatformLabel) {
    if (!allowReserveForMe) return { mode: "directProvider" }
    return { mode: "menu", reservationPlatformLabel }
  }

  if (!allowReserveForMe) {
    return bookingUrl ? { mode: "directProvider" } : { mode: "none" }
  }

  return { mode: "reserveForMe" }
}

export function buildReserveTableQuestion({
  placeName,
  partySize,
  dateLabel,
  timeLabel,
}: {
  placeName: string
  partySize?: number
  dateLabel?: string
  timeLabel?: string
}): string {
  const details: string[] = []
  if (partySize && partySize > 0) {
    details.push(`for ${partySize} ${pluralize("person", partySize)}`)
  }
  if (dateLabel) details.push(`on ${dateLabel}`)
  if (dateLabel && timeLabel) {
    details.push(`at ${timeLabel}`)
  }
  return `Can you reserve a table at ${placeName}${
    details.length ? ` ${details.join(" ")}` : ""
  }?`
}

export function appendReservationParamsToBookingUrl(
  url: string,
  {
    date,
    partySize,
    time,
  }: {
    date?: DayObject
    partySize?: number
    time?: TimeOfDay
  }
): string {
  const provider = reservationUrlProvider(url)
  const urlObj = tryParseUrl(url)
  if (!provider || !urlObj) {
    return url
  }

  const dateStr = date ? dayObjectAsUrlParam(date) : undefined
  const timeStr = time ? timeOfDayToKey(time) : undefined

  if (provider === "opentable") {
    if (dateStr && timeStr) {
      urlObj.searchParams.set("dateTime", `${dateStr}T${timeStr}`)
    }
    if (partySize && partySize > 0) {
      urlObj.searchParams.set("covers", String(partySize))
    }
  } else if (provider === "resy") {
    if (dateStr) {
      urlObj.searchParams.set("date", dateStr)
    }
    if (partySize && partySize > 0) {
      urlObj.searchParams.set("seats", String(partySize))
    }
    if (timeStr) {
      // Resy expects HHMM without a colon, e.g. time=1900
      urlObj.searchParams.set("time", timeStr.replace(":", ""))
    }
  } else if (provider === "thefork") {
    const hashParts = ["booking="]
    if (dateStr) hashParts.push(`date=${dateStr}`)
    if (timeStr) hashParts.push(`hour=${encodeURIComponent(timeStr)}`)
    if (partySize && partySize > 0) {
      hashParts.push(`partySize=${partySize}`)
    }
    urlObj.hash = hashParts.join("&")
  } else {
    if (dateStr) {
      urlObj.searchParams.set("date", dateStr)
    }
    if (partySize && partySize > 0) {
      urlObj.searchParams.set("size", String(partySize))
    }
    if (timeStr) {
      urlObj.searchParams.set("time", timeStr)
    }
  }

  return urlObj.toString()
}
